// @ts-check
import { MessageSquare, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import atelierAdminService, { STAGES, STAGE_LABELS } from '../services/atelierAdminService';

export default function AtelierProjectsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [unread, setUnread] = useState({});
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, unreadList] = await Promise.all([
        atelierAdminService.listProjects({ stage: stage || undefined }),
        atelierAdminService.unreadByProject().catch(() => []),
      ]);
      setProjects(Array.isArray(list) ? list : []);
      const map = {};
      (Array.isArray(unreadList) ? unreadList : []).forEach((u) => {
        map[u.project_id] = u.count;
      });
      setUnread(map);
    } catch (e) {
      toast.error(e.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [stage, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Atelier Projects</h1>
          <p className="text-sm text-muted-foreground">Bespoke manufacturing pipeline.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-accent" title="Refresh">
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="mb-4">
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All stages</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {STAGE_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-card border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Ref</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Qty</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr
                key={p.id}
                className="border-t hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate(`/atelier/projects/${p.id}`)}
              >
                <td className="px-4 py-3 font-mono">{p.reference_code}</td>
                <td className="px-4 py-3 font-medium flex items-center gap-2">
                  {p.title}
                  {unread[p.id] > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs bg-red-500 text-white rounded-full px-2 py-0.5">
                      <MessageSquare size={11} /> {unread[p.id]}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 capitalize">{p.project_type?.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3">{p.quantity}</td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 rounded-full text-xs bg-muted">{STAGE_LABELS[p.stage] || p.stage}</span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-blue-600 dark:text-blue-400">Open →</span>
                </td>
              </tr>
            ))}
            {!loading && projects.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground/70">
                  No projects
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
